import React, { useEffect, useState } from "react";
import AOS from "aos";
import "aos/dist/aos.css";
import { useTranslation } from "react-i18next";
import axiosInstance from "../../axiosInstance";
import moment from "moment";
import { useAuth } from "../../AuthContext";
import { useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";

const OrganizerEvent = () => {
  const { t, i18n } = useTranslation();
  const languageDirection = i18n.language === "ar" ? "rtl" : "ltr";
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(10);
  const { userId } = useAuth();
  toast.dismiss();

  useEffect(() => {
    const fetchMyEvents = async () => {
      try {
        const response = await axiosInstance.get(`/api/v1/organizers/events`, {
          params: {
            Page: page,
            PageCount: pageCount,
          },
        });
        const events = response.data.items;
        setEvents(events);
      } catch (error) {
        console.error("Error fetching events:", error);
      }
    };

    fetchMyEvents();
  }, [page, pageCount]);

  const handleDelete = async (eventId) => {
    const isConfirmed = window.confirm("هل أنت متأكد أنك تريد حذف هذا الحدث؟");

    if (!isConfirmed) {
      return;
    }

    try {
      await axiosInstance.delete(`/api/v1/organizers/events/${eventId}`);
      toast.success("تم حذف الحدث بنجاح!");

      setEvents((prevEvents) =>
        prevEvents.filter((event) => event.id !== eventId)
      );
    } catch (error) {
      console.error("Error deleting event:", error);
      const errorMessage =
        error.response?.data?.detail || "حدث خطأ أثناء حذف الحدث.";

      toast.error(`${t("error_message")}: ${errorMessage}`);
    }
  };

  return (
    <div dir={languageDirection}>
      <section className="py-5">
        <div className="container">
          {/* Header */}
          <div className="row mb-4">
            <div className="col-lg-8">
              <h1 className="section-title">{t("event_management")}</h1>
              <p className="text-muted">{t("event_management_description")}</p>
            </div>
            <div className="col-lg-4 text-end">
              <button
                className="btn btn-primary"
                onClick={() => navigate("/create-event")}
              >
                <i className="fas fa-plus me-2"></i> {t("create_event")}
              </button>
            </div>
          </div>

          {/* Event Filters */}
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center">
              <h5 className="mb-0">{t("filter_events")}</h5>
              <div className="d-flex align-items-center">
                <label
                  htmlFor="eventStatusFilter"
                  className="me-2 mb-0 text-muted"
                >
                  {t("event_status")}:
                </label>
                <select
                  className="form-select form-select-sm"
                  style={{ width: "auto", minWidth: "200px" }}
                >
                  <option value="all">{t("all_events")}</option>
                  <option value="new">{t("new_events")}</option>
                  <option value="completed">{t("completed_events")}</option>
                </select>
              </div>
            </div>
            <hr className="mt-3 mb-4" />
          </div>

          {/* Event Cards */}
          <div className="row">
            {events.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-calendar-alt fa-3x mb-3 text-muted"></i>
                <h5 className="text-muted">{t("no_events_yet")}</h5>
                <p className="text-muted small">
                  {t("press_add_to_create_event")}
                </p>
              </div>
            ) : (
              events.map((event) => {
                const isPast = moment(event.endDate).isBefore(moment());
                const isUpcoming = moment(event.startDate).isAfter(moment());
                return (
                  <div className="col-md-6 col-lg-4 mb-4" key={event.id}>
                    <div className="card hover-card h-100 shadow-sm">
                      <div
                        className="card-img-top position-relative"
                        style={{ height: "160px" }}
                      >
                        <div
                          style={{
                            backgroundImage: `url(${
                              event.bannerUrl ||
                              "https://via.placeholder.com/400x200"
                            })`,
                            backgroundSize: "cover",
                            backgroundPosition: "center",
                            height: "100%",
                          }}
                        />

                        {/* الزاوية العلوية اليسرى */}
                        <div className="position-absolute top-0 start-0 p-2 d-flex flex-column align-items-start gap-1">
                          <span
                            className={`badge ${
                              event.isPublished
                                ? "bg-success"
                                : "bg-warning text-dark"
                            }`}
                          >
                            {t(event.isPublished ? "published" : "draft")}
                          </span>

                          {/* {event.languageName && (
                            <span className="badge bg-secondary">
                              {event.languageName}
                            </span>
                          )} */}
                        </div>

                        {/* الزاوية العلوية اليمنى */}
                        <div className="position-absolute top-0 end-0 p-2 d-flex flex-column align-items-end gap-1">
                          <span className="badge bg-primary">
                            {t("speakers_count")}:{" "}
                            {event.eventInvitations?.length || 0}
                          </span>
                          {/* 
                          {event.eventSpecialities?.length > 0 && (
                            <span className="badge bg-dark">
                              {event.eventSpecialities[0].name || "تخصص"}
                            </span>
                          )} */}
                        </div>
                      </div>

                      <div className="card-body">
                        <h5 className="card-title mb-2">{event.arabicName}</h5>
                        <div className="d-flex flex-wrap mb-3">
                          {event.topics?.map((topic, index) => (
                            <span
                              key={index}
                              className="badge rounded-pill bg-light text-dark me-1 mb-1 px-2 py-1"
                            >
                              <i className="fas fa-tag me-1"></i> {topic}
                            </span>
                          ))}
                        </div>

                        {/* Metadata */}
                        <div className="row g-2 mb-3">
                          <div className="col-12">
                            <div className="d-flex align-items-center">
                              <i className="fas fa-calendar-alt text-primary-custom me-2"></i>
                              <span>
                                {moment(event.eventOn).format("DD MMM YYYY")} -{" "}
                                {moment(event.eventEndOn).format("DD MMM YYYY")}
                              </span>
                            </div>
                          </div>
                          <div className="col-12">
                            <div className="d-flex align-items-center">
                              <i className="fas fa-map-marker-alt text-primary-custom me-2"></i>
                              <span>
                                {event.country?.name
                                  ? `${
                                      event.city?.name
                                        ? event.city.name + ", "
                                        : ""
                                    }${event.country.name}`
                                  : event.city?.name
                                  ? event.city.name
                                  : t("events_online")}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Invite/Request Info */}
                        <div className="row g-2">
                          <div className="col-6">
                            <div className="d-flex align-items-center p-2 border rounded bg-light h-100">
                              <div>
                                <small className="text-muted d-block">
                                  {t("pending_invitations")}
                                </small>
                                <span className="fw-bold text-warning">
                                  {event.eventInvitations?.length || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="col-6">
                            <div className="d-flex align-items-center p-2 border rounded bg-light h-100">
                              <div>
                                <small className="text-muted d-block">
                                  {t("pending_requests")}
                                </small>
                                <span className="fw-bold text-info">
                                  {event.eventInvitations?.filter(
                                    (inv) => inv.isRequested
                                  )?.length || 0}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="card-footer bg-white border-top-0 d-grid gap-2">
                        <a
                          className="btn btn-primary"
                          onClick={(e) => {
                            e.preventDefault();
                            navigate(`/organizer-event-detail/${event.id}`);
                            window.scrollTo(0, 0);
                          }}
                          href={`/organizer-event-detail/${event.id}`}
                        >
                          <i className="fas fa-cog me-1"></i>{" "}
                          {t("manage_event")}
                        </a>

                        <button
                          className="btn btn-outline-danger"
                          onClick={() => handleDelete(event.id)}
                        >
                          <i className="fas fa-trash me-1"></i> {t("delete")}
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
        <ToastContainer />
      </section>
    </div>
  );
};

export default OrganizerEvent;
